import { InformationMessageType } from "../../common-ui/information-message/informationMessageUtils";
import { BookingStatus } from "../../gql/graphql";
import { Colors } from "../colors";
import { Information } from "../texts";
import { exhaustSwitchCaseElseThrow } from "./ExhaustSwitchCase";
import {
  type GoodsApproval,
  GoodsApprovalStatus,
  GoodsApprovalType,
} from "./GoodsApproval";

export interface ApprovalConfig {
  backgroundColor: string | null;
  descriptionText: string;
  headerText: string;
  type: InformationMessageType | null;
}

export const getApprovalBackground = (
  status: GoodsApprovalStatus,
): string | null => {
  switch (status) {
    case GoodsApprovalStatus.Requested:
      return Colors.Alert4;
    case GoodsApprovalStatus.Approved:
      return Colors.Lush4;
    case GoodsApprovalStatus.Rejected:
      return Colors.Sos4;
    case GoodsApprovalStatus.NotRequested:
      return null;
    default:
      return exhaustSwitchCaseElseThrow(status);
  }
};

export const getApprovalAlertType = (
  status: GoodsApprovalStatus,
): InformationMessageType | null => {
  switch (status) {
    case GoodsApprovalStatus.Requested:
      return InformationMessageType.SoftWarning;
    case GoodsApprovalStatus.Approved:
      return InformationMessageType.Confirmed;
    case GoodsApprovalStatus.Rejected:
      return InformationMessageType.HardWarning;
    case GoodsApprovalStatus.NotRequested:
      return null;
    default:
      return exhaustSwitchCaseElseThrow(status);
  }
};

export const getApprovalHeader = (status: GoodsApprovalStatus): string => {
  switch (status) {
    case GoodsApprovalStatus.Requested:
      return "Request pending";
    case GoodsApprovalStatus.Approved:
      return "Approved";
    case GoodsApprovalStatus.Rejected:
      return "Not available";
    case GoodsApprovalStatus.NotRequested:
      return "";
    default:
      return exhaustSwitchCaseElseThrow(status);
  }
};

const getLoadingPreferenceDescription = (
  status: GoodsApprovalStatus,
): string => {
  switch (status) {
    case GoodsApprovalStatus.Requested:
      return Information.LoadingPreferenceRequested;
    case GoodsApprovalStatus.Approved:
      return Information.LoadingPreferenceApproved;
    case GoodsApprovalStatus.Rejected:
      return Information.LoadingPreferenceRejected;
    case GoodsApprovalStatus.NotRequested:
      return Information.LoadingPreferenceNotApplicable;
    default:
      return exhaustSwitchCaseElseThrow(status);
  }
};

const getCraneableStatusDescription = (status: GoodsApprovalStatus): string => {
  switch (status) {
    case GoodsApprovalStatus.Requested:
      return Information.CraneableRequested;
    case GoodsApprovalStatus.Approved:
      return Information.CraneableApproved;
    case GoodsApprovalStatus.Rejected:
      return Information.CraneableRejected;
    case GoodsApprovalStatus.NotRequested:
      return Information.CraneableNotApplicable;
    default:
      return exhaustSwitchCaseElseThrow(status);
  }
};

export const getHazardousDescription = (
  status: GoodsApprovalStatus,
  bookingStatus: BookingStatus | null,
  extendedTexts: boolean,
): string => {
  switch (status) {
    case GoodsApprovalStatus.Requested:
      return extendedTexts
        ? Information.HazardousGoodsRequested
        : Information.HazardousGoodsRequestedShort;
    case GoodsApprovalStatus.Approved:
      if (
        bookingStatus === BookingStatus.Confirmed ||
        bookingStatus === BookingStatus.Waitlist
      ) {
        return Information.HazardousGoodsApprovedLimitedEdit;
      }
      return Information.HazardousGoodsApproved;
    case GoodsApprovalStatus.Rejected:
    case GoodsApprovalStatus.NotRequested:
      return ""; // Hazardous does not have Rejected nor NotApplicable
    default:
      return exhaustSwitchCaseElseThrow(status);
  }
};

const getApprovalDescription = (
  goodsApproval: GoodsApproval,
  bookingStatus: BookingStatus | null,
  extendedTexts: boolean,
): string => {
  switch (goodsApproval.type) {
    case GoodsApprovalType.Hazardous:
      return getHazardousDescription(
        goodsApproval.status,
        bookingStatus,
        extendedTexts,
      );
    case GoodsApprovalType.LoadingPreference:
      return getLoadingPreferenceDescription(goodsApproval.status);
    case GoodsApprovalType.CraneableStatus:
      return getCraneableStatusDescription(goodsApproval.status);
  }
};

// TODO: Consider refactoring getApprovalInfo entirely / getting rid of it. Currently this is too abstract and handles hazardous, loading preference, etc.
//       which have different rules for when they should be shown, what text they should show, etc.
//       It might be better to have separate functions for each type of approval instead of trying to handle them all in one function.
export const getApprovalInfo = (
  goodsApproval: GoodsApproval,
  bookingStatus: BookingStatus | null = null,
  extendedTexts: boolean = false,
): ApprovalConfig | null => {
  if (goodsApproval.status === GoodsApprovalStatus.NotRequested) {
    return null;
  }
  return {
    backgroundColor: getApprovalBackground(goodsApproval.status),
    descriptionText: getApprovalDescription(
      goodsApproval,
      bookingStatus,
      extendedTexts,
    ),
    headerText: getApprovalHeader(goodsApproval.status),
    type: getApprovalAlertType(goodsApproval.status),
  };
};
